import type {
  AgentChatMessage,
  AutofixBatchRecord,
  ManagedPr,
  ManagedPrEventRecord,
} from "./types";

export interface AutofixIteration {
  /** The trigger user messages for this iteration (may be batched). */
  userMessages: AgentChatMessage[];
  /** The agent response. Null when the agent hasn't replied yet. */
  agentMessage: AgentChatMessage | null;
  /** Human-readable label, e.g. "4 CI Failures" or "CI Failure, Review Comment". */
  label: string;
  /** Total count of individual issues represented by userMessages. */
  issueCount: number;
}

/**
 * A skip whose backend handler ran a side effect (e.g. registering a Vercel
 * preview URL), aggregated across skip rows carrying the same action.
 */
export interface AutofixSkipActionSummary {
  /** Stable `outcome_details.action` id, e.g. "preview_registered". */
  id: string | null;
  /** Backend-stamped display verb, e.g. "Registered preview". */
  verb: string;
  /** Most recent `outcome_details.action_url`, when the action carries one. */
  url: string | null;
  /** Distinct bot logins in first-appearance order. */
  botLogins: string[];
  count: number;
}

/**
 * Aggregated bot-noop skips absorbed into an autofix group (or a skip-only
 * group with no real work). The pill title uses this only when there are no
 * real issues; otherwise the popover renders it inline.
 */
export interface AutofixSkippedFeedback {
  /** Distinct `outcome_details.bot_login` values of plain-ignored rows. */
  botLogins: string[];
  /** Total number of skipped comments (plain ignores + handler actions). */
  count: number;
  /**
   * Ignored review-comment bodies (from each plain-ignored skip row's
   * event payload) so the muted row can expand to show what was filtered.
   */
  comments: AutofixReviewCommentPreview[];
  /** Skips whose handler ran a side effect, grouped per action. */
  actions: AutofixSkipActionSummary[];
  /**
   * When the newest skip settled; positions the block in the feed's
   * completion order. Absent on aggregates built before this field existed
   * (e.g. cached fixtures), which keep the legacy bottom slot.
   */
  latestAt?: Date;
}

/** Number of skips that were plain ignores (no handler action). */
export function ignoredSkipCount(feedback: AutofixSkippedFeedback): number {
  return (
    feedback.count -
    feedback.actions.reduce((total, action) => total + action.count, 0)
  );
}

export interface AutofixGroup {
  /** Stable ID derived from the first message in the group. */
  id: string;
  /** Each back-and-forth between trigger messages and agent response. */
  iterations: AutofixIteration[];
  /** Sum of issueCount across all iterations. */
  totalIssues: number;
  /** True while the last iteration is still awaiting or streaming a response. */
  isActive: boolean;
  /**
   * Autofix user messages that arrived while the agent is still working on
   * the current iteration. Shown under an "Up next" section in the UI.
   */
  pendingMessages: AgentChatMessage[];
  /** Human-readable label for the pending queue, e.g. "3 CI Failures". */
  pendingLabel: string;
  /** Total issue count across pending messages. */
  pendingCount: number;
  /**
   * Set of every message ID absorbed into this group (user + agent messages
   * from all iterations and pending messages). Used by scrollToMessageId to
   * locate grouped messages that aren't the representative `msg.id`.
   */
  allMessageIds: Set<string>;
  /**
   * Bot comments the no-op filter skipped while this group was collecting.
   * Null when none. Lives on the group so mixed work keeps them inside the
   * pill rather than as a sibling transcript row.
   */
  skippedFeedback: AutofixSkippedFeedback | null;
}

/**
 * The backend currently emits autofix message content prefixed with
 * `[Autofix]`. Historical messages in the database still carry the older
 * `[Auto-fix]` prefix, so we match both for backward compatibility.
 */
const AUTOFIX_PREFIXES = ["[Autofix]", "[Auto-fix]"] as const;

/** Returns the body after the autofix prefix, or null if `content` isn't prefixed. */
function stripAutofixPrefix(content: string): string | null {
  for (const prefix of AUTOFIX_PREFIXES) {
    if (content.startsWith(prefix)) return content.slice(prefix.length);
  }
  return null;
}

/**
 * Correlate managed_pr_events rows onto their trigger messages by
 * assistant_message_id (msg.autofixEvent), so trigger/skip/discard state is
 * read from the table-backed row. Mutates in place: the parent-thread rows
 * are cached per StreamMessage, and re-annotating a fresh payload's rows
 * refreshes settlement state without churning message identity. Messages
 * without a row (payloads from backends that predate managed_pr_events,
 * continuation sentinels) keep any broadcast-attached event or fall back to
 * the content prefix.
 */
export function attachAutofixEventRows(
  messages: AgentChatMessage[],
  events: ManagedPrEventRecord[] | undefined,
): void {
  if (!events?.length) return;
  const byMessageId = new Map<string, ManagedPrEventRecord>();
  for (const event of events) {
    if (event.assistant_message_id) {
      byMessageId.set(event.assistant_message_id, event);
    }
  }
  for (const msg of messages) {
    const event = byMessageId.get(msg.id);
    if (event) msg.autofixEvent = event;
  }
}

/**
 * Correlate each parent-session mirror message to the autofix_batches row
 * whose `mirror_message_id` points at it (msg.autofixMirrorBatch). Mutates
 * in place, same contract as attachAutofixEventRows. Mirrors without a
 * pointing batch (pre-mirror_message_id backends) keep the content-prefix
 * fallback.
 */
export function attachAutofixBatchPointers(
  messages: AgentChatMessage[],
  batches: AutofixBatchRecord[] | undefined,
): void {
  if (!batches?.length) return;
  const byMirrorMessageId = new Map<string, AutofixBatchRecord>();
  for (const batch of batches) {
    if (batch.mirror_message_id) {
      byMirrorMessageId.set(batch.mirror_message_id, batch);
    }
  }
  for (const msg of messages) {
    const batch = byMirrorMessageId.get(msg.id);
    if (batch) msg.autofixMirrorBatch = batch;
  }
}

/** Returns true when `msg` is an autofix user message (trigger). */
export function isAutofixMessage(msg: AgentChatMessage): boolean {
  if (msg.role !== "user") return false;
  // Prefer the event row, then the content prefix (messages that predate
  // managed_pr_events entirely).
  if (msg.autofixEvent) return true;
  return stripAutofixPrefix(msg.content ?? "") !== null;
}

/**
 * A display-only record of a bot comment the autofix no-op filter skipped.
 * Must be checked BEFORE isAutofixMessage folding: skip rows carry
 * an event row and the `[Autofix]` content prefix, so they would otherwise
 * be swallowed into AutofixGroup pills.
 */
export function isAutofixSkippedMessage(msg: {
  autofixEvent?: ManagedPrEventRecord;
}): boolean {
  return msg.autofixEvent?.outcome === "skipped";
}

/** True when the event settled as discarded (stale SHA, payment block). */
function isAutofixDiscardedMessage(msg: AgentChatMessage): boolean {
  return msg.autofixEvent?.outcome === "discarded";
}

/** Sanitized trigger payload from the event row. */
function autofixPayload(msg: AgentChatMessage): unknown {
  return msg.autofixEvent?.payload;
}

/** An autofix trigger that should fold into an AutofixGroup pill. */
function isAutofixTriggerMessage(msg: AgentChatMessage): boolean {
  return isAutofixMessage(msg) && !isAutofixSkippedMessage(msg);
}

/**
 * Sentinel messages written before `metadata.autofix_sentinel` existed
 * (pre-#7291) carry no metadata at all, so they are identified by their
 * fixed content prefix, mirroring the legacy `[Autofix]` prefix fallback.
 */
const AUTOFIX_SENTINEL_PREFIXES = [
  "Autofix enabled for ",
  "Autofix disabled for ",
] as const;

/** True for synthetic "Autofix enabled/disabled for ..." user messages. */
export function isAutofixSentinelMessage(message: {
  content?: string | null;
  metadata?: Record<string, unknown> | null;
}): boolean {
  if (message.metadata?.autofix_sentinel) return true;
  return AUTOFIX_SENTINEL_PREFIXES.some((prefix) =>
    (message.content ?? "").startsWith(prefix),
  );
}

/**
 * Returns true when `msg` is an autofix mirror message: a DB-only assistant
 * message the backend writes into the PR's originating session when an
 * autofix batch completes in its dedicated child session.
 *
 * Prefers the attached batch pointer (attachAutofixBatchPointers); falls
 * back to the mirror's fixed content prefix (same defense as
 * isAutofixMessage) so mirrors without a pointing batch degrade to a
 * collapsed pill rather than a raw assistant bubble.
 */
export function isAutofixMirrorMessage(msg: {
  role: string;
  content?: string | null;
  autofixMirrorBatch?: AutofixBatchRecord;
}): boolean {
  if (msg.role !== "agent") return false;
  if (msg.autofixMirrorBatch) return true;
  return MIRROR_CONTENT_PR_RE.test(msg.content ?? "");
}

export type AutofixType =
  | "ci_failure"
  | "review_comment"
  | "merge_conflict"
  | "initial_autofix"
  | "continuation"
  | "unknown";

function resolveAutofixType(msg: AgentChatMessage): AutofixType {
  if (msg.autofixEvent) {
    return msg.autofixEvent.event_type;
  }
  const body = stripAutofixPrefix(msg.content);
  if (body === null) return "unknown";
  if (body.startsWith(" CI failure")) return "ci_failure";
  if (body.startsWith(" Review comment")) return "review_comment";
  if (body.startsWith(" Merge conflict")) return "merge_conflict";
  if (body.startsWith(" Initial pass")) return "initial_autofix";
  if (body.startsWith(" Continuing")) return "continuation";
  return "unknown";
}

const TYPE_LABELS: Record<AutofixType, { singular: string; plural: string }> = {
  ci_failure: { singular: "CI Failure", plural: "CI Failures" },
  review_comment: { singular: "Review Comment", plural: "Review Comments" },
  merge_conflict: { singular: "Merge Conflict", plural: "Merge Conflicts" },
  // initial_autofix messages are composite; they get unpacked into their
  // constituent finding counts in buildIterationLabel, so these labels are
  // only used when no payload is available (legacy or malformed messages).
  initial_autofix: { singular: "Initial Pass", plural: "Initial Passes" },
  continuation: { singular: "Continuation", plural: "Continuations" },
  unknown: { singular: "Issue", plural: "Issues" },
};

/**
 * A composite `initial_autofix` message bundles multiple findings. Inspect its
 * event payload and return a per-type count so the label renders as though
 * each finding had been dispatched individually.
 *
 * Returns null if the payload is missing or empty; callers fall back to
 * counting the message as a single "Initial Pass".
 */
function countInitialAutofixFindings(
  msg: AgentChatMessage,
): Map<AutofixType, number> | null {
  const payload = autofixPayload(msg) as
    | {
        ci_failures?: unknown[];
        review_comments?: unknown[];
        merge_conflict?: unknown;
      }
    | undefined;
  if (!payload) return null;

  const ciCount = Array.isArray(payload.ci_failures)
    ? payload.ci_failures.length
    : 0;
  const reviewCount = Array.isArray(payload.review_comments)
    ? payload.review_comments.length
    : 0;
  const conflictCount = payload.merge_conflict ? 1 : 0;

  if (ciCount + reviewCount + conflictCount === 0) return null;

  const counts = new Map<AutofixType, number>();
  if (ciCount > 0) counts.set("ci_failure", ciCount);
  if (reviewCount > 0) counts.set("review_comment", reviewCount);
  if (conflictCount > 0) counts.set("merge_conflict", conflictCount);
  return counts;
}

/**
 * Per-type event counts for a batch of trigger user messages, with composite
 * `initial_autofix` payloads unpacked into their constituent findings.
 *
 * Mirrors the backend's `AutofixService::countBatchEvents()`, which produces
 * the canonical `issue_count` on batch records and mirror messages:
 * continuation sentinels are dispatch plumbing rather than items the batch
 * addressed, and events discarded as stale are excluded. Counting them here
 * would make the live pill's count disagree with the settled mirror's.
 */
export function countAutofixEventTypes(
  userMessages: readonly AgentChatMessage[],
): Map<AutofixType, number> {
  const counts = new Map<AutofixType, number>();
  for (const msg of userMessages) {
    if (isAutofixDiscardedMessage(msg)) continue;
    const type = resolveAutofixType(msg);
    if (type === "continuation") continue;
    if (type === "initial_autofix") {
      const findings = countInitialAutofixFindings(msg);
      if (findings) {
        findings.forEach((n, t) => {
          counts.set(t, (counts.get(t) ?? 0) + n);
        });
        continue;
      }
    }
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }
  return counts;
}

/** Opening or closing code fence: up to 3 spaces of indent, then ``` or ~~~. */
const CODE_FENCE_RE = /^ {0,3}(`{3,}|~{3,})/;

/**
 * Removes markdown that renders invisibly on GitHub: HTML comments and
 * link-reference-definition metadata lines (e.g. Vercel's `[vc]: #<base64>`
 * hidden payload). Snippets, rendered bodies, and has-visible-content checks
 * all use this so previews reflect what a reader actually sees. Reference
 * definitions with real URL destinations are kept, since stripping them
 * would break reference-style links in the body. Fenced code blocks are
 * preserved verbatim: inside a fence this markdown is visible literal text
 * (e.g. a comment quoting an HTML or markdown example), not hidden metadata.
 */
export function stripInvisibleCommentMarkdown(body: string): string {
  const out: string[] = [];
  // Prose lines pending a strip pass, buffered so HTML comments spanning
  // multiple lines between fences are still matched and removed as a unit.
  let prose: string[] = [];
  // Marker of the currently open fence (e.g. "```" or "~~~~"), or null.
  let openFence: string | null = null;

  const flushProse = () => {
    if (prose.length === 0) return;
    const stripped = prose
      .join("\n")
      .replace(/<!--[\s\S]*?-->/g, "")
      .split("\n")
      .filter((line) => !/^\[[^\]]*\]:\s*#\S*$/.test(line.trim()));
    out.push(...stripped);
    prose = [];
  };

  for (const line of body.split("\n")) {
    if (openFence) {
      out.push(line);
      const fence = CODE_FENCE_RE.exec(line);
      // A closing fence uses the same character and is at least as long.
      if (
        fence &&
        fence[1][0] === openFence[0] &&
        fence[1].length >= openFence.length
      ) {
        openFence = null;
      }
      continue;
    }
    const fence = CODE_FENCE_RE.exec(line);
    if (fence) {
      flushProse();
      out.push(line);
      openFence = fence[1];
      continue;
    }
    prose.push(line);
  }
  flushProse();
  return out.join("\n").trim();
}

/** A triggering review comment rendered as a preview on autofix pills. */
export interface AutofixReviewCommentPreview {
  id?: number;
  author?: string;
  body: string;
  path?: string | null;
  line?: number | null;
  url?: string | null;
}

/**
 * Shape of a sanitized review-comment payload as exposed by the backend
 * (AssistantMessage::PUBLIC_REVIEW_COMMENT_KEYS).
 */
interface ReviewCommentPayload {
  comment_id?: number;
  comment_body?: string;
  comment_path?: string | null;
  comment_line?: number | null;
  comment_url?: string | null;
  github_username?: string;
}

function previewFromPayload(
  payload: ReviewCommentPayload,
): AutofixReviewCommentPreview | null {
  const body =
    typeof payload.comment_body === "string" ? payload.comment_body : "";
  if (stripInvisibleCommentMarkdown(body).length === 0) return null;
  return {
    id: typeof payload.comment_id === "number" ? payload.comment_id : undefined,
    author:
      typeof payload.github_username === "string"
        ? payload.github_username
        : undefined,
    body,
    path:
      typeof payload.comment_path === "string" ? payload.comment_path : null,
    line:
      typeof payload.comment_line === "number" ? payload.comment_line : null,
    url: typeof payload.comment_url === "string" ? payload.comment_url : null,
  };
}

/**
 * Legacy trigger messages predate the sanitized public payload, so the only
 * preview source is the message content the backend writes:
 * `[Autofix] Review comment from @author: body` (body truncated to 500 bytes).
 */
const LEGACY_REVIEW_COMMENT_RE = /^ Review comment from @(\S+): ([\s\S]+)$/;

function previewFromLegacyContent(
  msg: AgentChatMessage,
): AutofixReviewCommentPreview | null {
  const body = stripAutofixPrefix(msg.content ?? "");
  if (body === null) return null;
  const match = LEGACY_REVIEW_COMMENT_RE.exec(body);
  if (!match) return null;
  // Legacy content is truncated to 500 bytes, which can leave nothing but an
  // invisible metadata line (e.g. a Vercel bot comment whose leading
  // `[vc]: #<base64>` blob exceeds the truncation limit). An empty-looking
  // card is worse than no preview.
  if (stripInvisibleCommentMarkdown(match[2]).length === 0) return null;
  return { author: match[1], body: match[2] };
}

/**
 * Collects the triggering review comments from a batch of autofix trigger
 * user messages so the pill popover can render them as a preview. Handles
 * standalone `review_comment` events, composite `initial_autofix` payloads,
 * and legacy messages whose only preview source is the message content.
 */
export function extractReviewCommentPreviews(
  userMessages: readonly AgentChatMessage[],
): AutofixReviewCommentPreview[] {
  const previews: AutofixReviewCommentPreview[] = [];
  for (const msg of userMessages) {
    if (isAutofixDiscardedMessage(msg)) continue;
    const type = resolveAutofixType(msg);

    if (type === "review_comment") {
      const payload = autofixPayload(msg) as ReviewCommentPayload | undefined;
      const preview =
        (payload ? previewFromPayload(payload) : null) ??
        previewFromLegacyContent(msg);
      if (preview) previews.push(preview);
      continue;
    }

    if (type === "initial_autofix") {
      const payload = autofixPayload(msg) as
        | { review_comments?: unknown[] }
        | undefined;
      if (!Array.isArray(payload?.review_comments)) continue;
      for (const item of payload.review_comments) {
        if (typeof item !== "object" || item === null) continue;
        const preview = previewFromPayload(item as ReviewCommentPayload);
        if (preview) previews.push(preview);
      }
    }
  }
  return previews;
}

/** A triggering CI failure rendered as a preview on autofix pills. */
export interface AutofixCiFailurePreview {
  jobName: string;
  workflowName?: string | null;
}

/**
 * Shape of a sanitized ci_failure payload as exposed by the backend
 * (AssistantMessage::PUBLIC_CI_FAILURE_KEYS).
 */
interface CiFailurePayload {
  workflow_name?: string;
  job_name?: string;
  run_id?: string | number;
}

function ciPreviewFromPayload(
  payload: CiFailurePayload,
): AutofixCiFailurePreview | null {
  if (typeof payload.job_name !== "string" || payload.job_name.length === 0) {
    return null;
  }
  return {
    jobName: payload.job_name,
    workflowName:
      typeof payload.workflow_name === "string" ? payload.workflow_name : null,
  };
}

/**
 * Legacy trigger messages predate the sanitized public payload, so the only
 * preview source is the message content the backend writes:
 * `[Autofix] CI failure in <workflow> workflow. Failed jobs: <job>`.
 */
const LEGACY_CI_FAILURE_RE =
  /^ CI failure in ([\s\S]+?) workflow\. Failed jobs: ([\s\S]+)$/;

function ciPreviewFromLegacyContent(
  msg: AgentChatMessage,
): AutofixCiFailurePreview | null {
  const body = stripAutofixPrefix(msg.content ?? "");
  if (body === null) return null;
  const match = LEGACY_CI_FAILURE_RE.exec(body);
  if (!match) return null;
  return { jobName: match[2].trim(), workflowName: match[1].trim() };
}

/**
 * Collects the triggering CI failures from a batch of autofix trigger user
 * messages so the pill popover can render the failed job names as a preview.
 * Handles standalone `ci_failure` events, composite `initial_autofix`
 * payloads, and legacy messages whose only preview source is the content.
 */
export function extractCiFailurePreviews(
  userMessages: readonly AgentChatMessage[],
): AutofixCiFailurePreview[] {
  const previews: AutofixCiFailurePreview[] = [];
  for (const msg of userMessages) {
    if (isAutofixDiscardedMessage(msg)) continue;
    const type = resolveAutofixType(msg);

    if (type === "ci_failure") {
      const payload = autofixPayload(msg) as CiFailurePayload | undefined;
      const preview =
        (payload ? ciPreviewFromPayload(payload) : null) ??
        ciPreviewFromLegacyContent(msg);
      if (preview) previews.push(preview);
      continue;
    }

    if (type === "initial_autofix") {
      const payload = autofixPayload(msg) as
        | { ci_failures?: unknown[] }
        | undefined;
      if (!Array.isArray(payload?.ci_failures)) continue;
      for (const item of payload.ci_failures) {
        if (typeof item !== "object" || item === null) continue;
        const preview = ciPreviewFromPayload(item as CiFailurePayload);
        if (preview) previews.push(preview);
      }
    }
  }
  return previews;
}

/**
 * Builds a label and count from a batch of trigger user messages.
 * e.g. "4 CI Failures" or "2 CI Failures, 1 Review Comment"
 */
export function buildIterationLabel(userMessages: AgentChatMessage[]): {
  label: string;
  count: number;
} {
  const counts = countAutofixEventTypes(userMessages);

  const parts: string[] = [];
  let total = 0;
  counts.forEach((n, type) => {
    total += n;
    const labels = TYPE_LABELS[type] ?? TYPE_LABELS.unknown;
    parts.push(n === 1 ? labels.singular : `${n} ${labels.plural}`);
  });

  // Iterations made up entirely of uncounted messages (a continuation
  // sentinel, discarded events) still need a feed label; they just
  // contribute no items to the batch's count.
  if (parts.length === 0 && userMessages.length > 0) {
    return { label: TYPE_LABELS.continuation.singular, count: 0 };
  }

  return { label: parts.join(", "), count: total };
}

/**
 * One child-session feed slice backing a segment's pill. A segment normally
 * has one feed, but autofix child sessions rotate per batch window, so a
 * PR's consecutive batches can span several child sessions; the pill loads
 * every feed so its counts and popover cover all of the segment's work.
 */
export interface AutofixSegmentFeed {
  /** Undefined when the child session is unknown (stripped legacy mirror). */
  childSessionId?: string;
  /**
   * Inclusive child-session message-ID bounds covering this feed's batches,
   * resolved from the batch rows pointing at its mirrors. Set only when
   * every mirror in the feed resolved a completed batch on this child
   * session; otherwise the pill falls back to the segment's timestamp
   * window.
   */
  startMessageId?: string;
  endMessageId?: string;
}

/**
 * One per-PR "autofix subagent" pill in the parent transcript, backed by
 * dedicated child sessions. A segment covers the child-session activity
 * between two parent-conversation messages: consecutive mirror messages for
 * the same PR with no intervening parent message collapse into one segment;
 * the next parent message starts a new one.
 */
export interface AutofixChildSegment {
  /** Stable ID derived from the first mirror message (or PR for live-only). */
  id: string;
  /** Groups mirrors of the same PR: managed_pr_id, else child session id. */
  prKey: string;
  managedPrId?: string;
  /** The segment's latest child session (the last feed's, live tail). */
  childSessionId?: string;
  repository?: string;
  prNumber?: number;
  /** Mirror messages absorbed into this segment, chronological. */
  mirrors: AgentChatMessage[];
  /** Sum of per-batch issue counts (1 per mirror without a batch row). */
  mirroredIssueCount: number;
  /**
   * Summed per-type event counts across the segment's batches, so the
   * collapsed pill can name the event type ("3 comments") before any
   * child feed loads. Null when any mirror lacks counts (legacy): a
   * partial sum would understate the segment, so the title falls back to
   * the generic "N items".
   */
  mirroredEventCounts: Map<AutofixType, number> | null;
  /** Outcome of the segment's last completed batch. */
  lastOutcome?: "completed" | "failed";
  /**
   * True while the PR is being autofixed in its child session and this
   * segment is the transcript tail (no parent message after it) — the
   * running batch belongs to this segment.
   */
  isActive: boolean;
  /**
   * Child-session feed slices, chronological: consecutive mirrors sharing a
   * child session collapse into one feed; a batch-window rotation to a fresh
   * child session starts the next.
   */
  feeds: AutofixSegmentFeed[];
  /**
   * Legacy child-session time window this segment covers: from the parent
   * message preceding the segment to the parent message following it. Used
   * to filter the child sub-conversation for feeds that couldn't resolve
   * message-ID bounds (mirrors that predate batch records).
   */
  windowStart?: Date;
  windowEnd?: Date;
  /**
   * Sum of mirrored batch wall-clock durations in ms (batch row
   * timestamps). Fallback working-time label until the child feed loads.
   */
  mirroredDurationMs: number;
  /** Mirror message IDs absorbed into this segment (for deep links). */
  allMessageIds: Set<string>;
}

interface MirrorInfo {
  prKey: string;
  managedPrId?: string;
  childSessionId?: string;
  repository?: string;
  prNumber?: number;
  issueCount: number;
  /** Per-type event counts from the batch record. */
  eventCounts: Map<AutofixType, number> | null;
  outcome?: "completed" | "failed";
  durationMs: number;
  /** Batch record whose mirror_message_id points at this mirror message. */
  batch?: AutofixBatchRecord;
}

const MIRROR_CONTENT_PR_RE = /^Autofix ran on (\S+)#(\d+)/;

function parseMirrorInfo(msg: AgentChatMessage): MirrorInfo {
  const batch = msg.autofixMirrorBatch;

  // The mirror's fixed content prefix is the only source of the PR's
  // repository#number identity (the batch row carries managed_pr_id, not
  // the display name).
  let repository: string | undefined;
  let prNumber: number | undefined;
  const match = MIRROR_CONTENT_PR_RE.exec(msg.content ?? "");
  if (match) {
    repository = match[1];
    prNumber = Number(match[2]);
  }

  const issueCount =
    batch?.issue_count != null && batch.issue_count > 0 ? batch.issue_count : 1;

  const eventCounts = parseEventCounts(batch?.event_counts);

  const outcome =
    batch?.outcome === "failure"
      ? ("failed" as const)
      : batch?.outcome === "success"
      ? ("completed" as const)
      : undefined;

  let durationMs = 0;
  if (batch?.completed_at) {
    const startedAt = Date.parse(batch.created_at);
    const completedAt = Date.parse(batch.completed_at);
    if (!Number.isNaN(startedAt) && !Number.isNaN(completedAt)) {
      durationMs = Math.max(0, completedAt - startedAt);
    }
  }

  return {
    prKey:
      batch?.managed_pr_id ??
      batch?.child_session_id ??
      (repository && prNumber !== undefined
        ? `${repository}#${prNumber}`
        : msg.id),
    managedPrId: batch?.managed_pr_id,
    childSessionId: batch?.child_session_id,
    repository,
    prNumber,
    issueCount,
    eventCounts,
    outcome,
    durationMs,
    batch,
  };
}

/** Per-type event counts (`{"review_comment": 3}` → Map), null when absent. */
export function parseEventCounts(
  value: unknown,
): Map<AutofixType, number> | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const counts = new Map<AutofixType, number>();
  for (const [type, count] of Object.entries(value)) {
    if (typeof count !== "number" || count <= 0) return null;
    counts.set(type as AutofixType, count);
  }
  return counts.size > 0 ? counts : null;
}

/**
 * Partition a run of consecutive mirror messages into one segment per PR,
 * preserving the order in which each PR first appears in the run.
 */
function segmentsFromMirrorRun(
  run: { msg: AgentChatMessage; originalIndex: number }[],
  windowStart: Date | undefined,
  windowEnd: Date | undefined,
): {
  segment: AutofixChildSegment;
  msg: AgentChatMessage;
  originalIndex: number;
}[] {
  const byPr = new Map<
    string,
    {
      info: MirrorInfo;
      entries: { msg: AgentChatMessage; originalIndex: number }[];
      infos: MirrorInfo[];
      issueCount: number;
      durationMs: number;
      lastOutcome?: "completed" | "failed";
    }
  >();

  for (const entry of run) {
    const info = parseMirrorInfo(entry.msg);
    const existing = byPr.get(info.prKey);
    if (existing) {
      existing.entries.push(entry);
      existing.infos.push(info);
      existing.issueCount += info.issueCount;
      existing.durationMs += info.durationMs;
      if (info.outcome) existing.lastOutcome = info.outcome;
      // Later mirrors may resolve richer batch data than earlier ones.
      existing.info = {
        ...existing.info,
        managedPrId: existing.info.managedPrId ?? info.managedPrId,
        childSessionId: info.childSessionId ?? existing.info.childSessionId,
        repository: existing.info.repository ?? info.repository,
        prNumber: existing.info.prNumber ?? info.prNumber,
      };
    } else {
      byPr.set(info.prKey, {
        info,
        entries: [entry],
        infos: [info],
        issueCount: info.issueCount,
        durationMs: info.durationMs,
        lastOutcome: info.outcome,
      });
    }
  }

  return Array.from(byPr.values()).map(
    ({ info, entries, infos, issueCount, durationMs, lastOutcome }) => {
      const first = entries[0];

      return {
        segment: {
          id: `autofix-child-${first.msg.id}`,
          prKey: info.prKey,
          managedPrId: info.managedPrId,
          childSessionId: info.childSessionId,
          repository: info.repository,
          prNumber: info.prNumber,
          mirrors: entries.map((e) => e.msg),
          mirroredIssueCount: issueCount,
          mirroredEventCounts: sumEventCounts(infos),
          lastOutcome,
          isActive: false,
          feeds: segmentFeedsFromInfos(infos),
          windowStart,
          windowEnd,
          mirroredDurationMs: durationMs,
          allMessageIds: new Set(entries.map((e) => e.msg.id)),
        },
        msg: first.msg,
        originalIndex: first.originalIndex,
      };
    },
  );
}

/** Summed per-type counts, null when any mirror lacks them (partial sums lie). */
function sumEventCounts(infos: MirrorInfo[]): Map<AutofixType, number> | null {
  const total = new Map<AutofixType, number>();
  for (const info of infos) {
    if (!info.eventCounts) return null;
    info.eventCounts.forEach((count, type) => {
      total.set(type, (total.get(type) ?? 0) + count);
    });
  }
  return total.size > 0 ? total : null;
}

/**
 * Chunk a segment's mirrors (chronological) into one feed per contiguous
 * child-session run: batches stay in one child session until a batch-window
 * rotation moves the PR to a fresh one, so each rotation starts a new feed.
 *
 * A feed's ID bounds cover it only when every one of its mirrors resolved a
 * batch with both bounds on that child session; a partial slice would
 * silently hide work, so mixed/legacy feeds keep the timestamp window.
 */
function segmentFeedsFromInfos(infos: MirrorInfo[]): AutofixSegmentFeed[] {
  const chunks: { childSessionId?: string; batches: MirrorInfo["batch"][] }[] =
    [];
  for (const info of infos) {
    const last = chunks[chunks.length - 1];
    if (last && last.childSessionId === info.childSessionId) {
      last.batches.push(info.batch);
    } else {
      chunks.push({
        childSessionId: info.childSessionId,
        batches: [info.batch],
      });
    }
  }

  return chunks.map(({ childSessionId, batches }) => {
    const boundsComplete = batches.every(
      (batch) =>
        batch !== undefined &&
        batch.end_message_id !== null &&
        batch.child_session_id === childSessionId,
    );
    return {
      childSessionId,
      startMessageId: boundsComplete ? batches[0]?.start_message_id : undefined,
      endMessageId: boundsComplete
        ? batches[batches.length - 1]?.end_message_id ?? undefined
        : undefined,
    };
  });
}

export type VisibleEntry =
  | {
      kind: "message";
      msg: AgentChatMessage;
      originalIndex: number;
      /**
       * Row the transcript would normally drop, kept because the
       * "Show hidden messages" debug toggle is on. Renders faded.
       */
      isInvisible?: boolean;
    }
  | {
      kind: "autofix_group";
      group: AutofixGroup;
      msg: AgentChatMessage;
      originalIndex: number;
    }
  | {
      kind: "autofix_child_group";
      segment: AutofixChildSegment;
      msg: AgentChatMessage;
      originalIndex: number;
    }
  | {
      kind: "review_loop";
      entries: VisibleEntry[];
      iterations: number;
      allMessageIds: ReadonlySet<string>;
      msg: AgentChatMessage;
      originalIndex: number;
    };

/** Folds one action occurrence into the per-action aggregates. */
function addSkipAction(
  actions: AutofixSkipActionSummary[],
  occurrence: {
    id: string | null;
    verb: string;
    url: string | null;
    botLogins: readonly string[];
    count: number;
  },
): void {
  const key = occurrence.id ?? occurrence.verb;
  let action = actions.find((a) => (a.id ?? a.verb) === key);
  if (!action) {
    action = {
      id: occurrence.id,
      verb: occurrence.verb,
      url: null,
      botLogins: [],
      count: 0,
    };
    actions.push(action);
  }
  action.count += occurrence.count;
  // Latest occurrence wins so idempotent re-registrations (e.g. redeploys)
  // surface the freshest URL.
  if (occurrence.url) action.url = occurrence.url;
  for (const login of occurrence.botLogins) {
    if (login && !action.botLogins.includes(login)) {
      action.botLogins.push(login);
    }
  }
}

/** Display verbs for skip-action ids stamped before outcome_details.action_verb existed. */
const LEGACY_SKIP_ACTION_VERBS: Record<string, string> = {
  preview_registered: "Registered preview",
};

export function summarizeSkippedRun(
  run: readonly { msg: AgentChatMessage }[],
): AutofixSkippedFeedback {
  const botLogins: string[] = [];
  const actions: AutofixSkipActionSummary[] = [];
  const ignoredMessages: AgentChatMessage[] = [];
  let latestAt: Date | undefined;
  for (const { msg } of run) {
    const settledAt = skipSettledAt(msg);
    if (settledAt && (!latestAt || settledAt > latestAt)) latestAt = settledAt;
    const details = msg.autofixEvent?.outcome_details;
    const login = details?.bot_login;
    // Rows written before action_verb landed in outcome_details carry only
    // the action id; recover the display verb from the known handler set.
    const verb =
      details?.action_verb ??
      (details?.action ? LEGACY_SKIP_ACTION_VERBS[details.action] : undefined);
    if (typeof verb === "string" && verb) {
      const id = details?.action;
      const url = details?.action_url;
      addSkipAction(actions, {
        id: typeof id === "string" ? id : null,
        verb,
        url: typeof url === "string" ? url : null,
        botLogins: typeof login === "string" && login ? [login] : [],
        count: 1,
      });
      continue;
    }
    if (typeof login === "string" && login && !botLogins.includes(login)) {
      botLogins.push(login);
    }
    ignoredMessages.push(msg);
  }
  // Skip rows carry event_type=review_comment + a sanitized payload, so the
  // same preview extractor used by real review-comment triggers works.
  // Action rows are self-descriptive (verb + URL) and skip the body preview.
  const comments = extractReviewCommentPreviews(ignoredMessages);
  return { botLogins, count: run.length, comments, actions, latestAt };
}

/** When a skip settled: the event row's processed_at, else the row's timestamp. */
function skipSettledAt(msg: AgentChatMessage): Date | undefined {
  const processedAt = msg.autofixEvent?.processed_at;
  if (processedAt) {
    const parsed = new Date(processedAt);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return msg.timestamp;
}

export function mergeSkippedFeedback(
  ...parts: Array<AutofixSkippedFeedback | null | undefined>
): AutofixSkippedFeedback | null {
  const botLogins: string[] = [];
  const comments: AutofixReviewCommentPreview[] = [];
  const actions: AutofixSkipActionSummary[] = [];
  let count = 0;
  let latestAt: Date | undefined;
  for (const part of parts) {
    if (!part || part.count <= 0) continue;
    count += part.count;
    for (const login of part.botLogins) {
      if (login && !botLogins.includes(login)) botLogins.push(login);
    }
    comments.push(...part.comments);
    for (const action of part.actions) {
      addSkipAction(actions, action);
    }
    if (part.latestAt && (!latestAt || part.latestAt > latestAt)) {
      latestAt = part.latestAt;
    }
  }
  return count > 0 ? { botLogins, count, comments, actions, latestAt } : null;
}

/** One renderable item of an autofix pill's peek feed, in display order. */
export type AutofixFeedItem =
  | { kind: "iteration"; iteration: AutofixIteration; index: number }
  | { kind: "skipped"; feedback: AutofixSkippedFeedback };

/**
 * When the iteration's agent response settled, or null while it is still
 * awaiting or streaming one. `updatedTimestamp` is the settle time when the
 * backend provides it; otherwise the turn start plus its working time
 * approximates it.
 */
export function iterationCompletedAt(iteration: AutofixIteration): Date | null {
  const agent = iteration.agentMessage;
  if (!agent || agent.isStreaming || agent.isProcessing) return null;
  if (agent.updatedTimestamp) return agent.updatedTimestamp;
  if (agent.durationMs != null) {
    return new Date(agent.timestamp.getTime() + agent.durationMs);
  }
  return agent.timestamp;
}

/**
 * The pill feed in completion order: items sort by when they finished, and
 * unfinished work sorts last. Skips settle the moment they land, so the
 * aggregated skip block sits before the first iteration that is still
 * running or that settled after the newest skip, so an in-flight iteration
 * always renders below it. At most one item is ever in flight, so
 * the order is append-only: nothing reshuffles when the running iteration
 * settles, and live and replay renders agree.
 */
export function orderAutofixFeedItems(
  group: Pick<AutofixGroup, "iterations" | "skippedFeedback">,
): AutofixFeedItem[] {
  const items: AutofixFeedItem[] = group.iterations.map((iteration, index) => ({
    kind: "iteration",
    iteration,
    index,
  }));
  const feedback = group.skippedFeedback;
  if (!feedback || feedback.count <= 0) return items;

  const latestAt = feedback.latestAt;
  let insertAt = group.iterations.length;
  if (latestAt) {
    for (let i = 0; i < group.iterations.length; i++) {
      const completedAt = iterationCompletedAt(group.iterations[i]);
      if (completedAt === null || completedAt > latestAt) {
        insertAt = i;
        break;
      }
    }
  }
  items.splice(insertAt, 0, { kind: "skipped", feedback });
  return items;
}

/**
 * A run of only bot-noop skips collapses into a skip-only autofix group so the
 * transcript renders an "Ignored N events" pill (same chrome as real work).
 */
function buildSkippedOnlyGroupEntry(
  run: { msg: AgentChatMessage; originalIndex: number }[],
): Extract<VisibleEntry, { kind: "autofix_group" }> {
  const skippedFeedback = summarizeSkippedRun(run);
  const allMessageIds = new Set(run.map((entry) => entry.msg.id));
  return {
    kind: "autofix_group",
    group: {
      id: `autofix-skipped-${run[0].msg.id}`,
      iterations: [],
      totalIssues: 0,
      isActive: false,
      pendingMessages: [],
      pendingLabel: "",
      pendingCount: 0,
      allMessageIds,
      skippedFeedback,
    },
    msg: run[0].msg,
    originalIndex: run[0].originalIndex,
  };
}

export type ReviewLoopEntry = Extract<VisibleEntry, { kind: "review_loop" }>;

export interface GroupAutofixMessagesOptions {
  /**
   * Autofix batch records for the session's managed PRs. Correlated onto
   * mirror messages via each batch's `mirror_message_id` pointer
   * (attachAutofixBatchPointers) to get the child-session message-ID
   * bounds of the batch each mirror reports.
   */
  autofixBatches?: AutofixBatchRecord[];
}

/**
 * Scans a flat, pre-filtered message list and groups consecutive autofix
 * user+agent pairs into `AutofixGroup` objects. Non-autofix messages pass
 * through unchanged.
 *
 * The returned array can be fed directly to the virtualizer — each entry
 * carries a stable `msg.id` for keying.
 */
export function groupAutofixMessages(
  messages: { msg: AgentChatMessage; originalIndex: number }[],
  options: GroupAutofixMessagesOptions = {},
): VisibleEntry[] {
  const result: VisibleEntry[] = [];
  attachAutofixBatchPointers(
    messages.map((entry) => entry.msg),
    options.autofixBatches,
  );

  let i = 0;
  while (i < messages.length) {
    const { msg, originalIndex } = messages[i];

    // Mirror messages collapse into per-PR "autofix subagent" segments.
    // A run of consecutive mirrors shares one parent-conversation gap; the
    // window is bounded by the surrounding non-mirror messages.
    if (isAutofixMirrorMessage(msg)) {
      const run: { msg: AgentChatMessage; originalIndex: number }[] = [];
      const windowStart = i > 0 ? messages[i - 1].msg.timestamp : undefined;
      while (i < messages.length && isAutofixMirrorMessage(messages[i].msg)) {
        run.push(messages[i]);
        i++;
      }
      const windowEnd =
        i < messages.length ? messages[i].msg.timestamp : undefined;
      for (const entry of segmentsFromMirrorRun(run, windowStart, windowEnd)) {
        result.push({ kind: "autofix_child_group", ...entry });
      }
      continue;
    }

    // A run of consecutive skipped-bot-comment records collapses into a
    // skip-only autofix group ("Ignored N events" pill).
    if (isAutofixSkippedMessage(msg)) {
      const run: { msg: AgentChatMessage; originalIndex: number }[] = [];
      while (i < messages.length && isAutofixSkippedMessage(messages[i].msg)) {
        run.push(messages[i]);
        i++;
      }
      result.push(buildSkippedOnlyGroupEntry(run));
      continue;
    }

    if (!isAutofixMessage(msg)) {
      result.push({ kind: "message", msg, originalIndex });
      i++;
      continue;
    }

    const iterations: AutofixIteration[] = [];
    const groupFirstMsg = msg;
    const groupFirstIndex = originalIndex;
    // Skip runs consumed while collecting triggers or pairing a reply;
    // folded into the group's skippedFeedback so they render inside the pill.
    const deferredSkipRuns: {
      msg: AgentChatMessage;
      originalIndex: number;
    }[][] = [];

    while (i < messages.length && isAutofixTriggerMessage(messages[i].msg)) {
      const batchUserMessages: AgentChatMessage[] = [];
      while (i < messages.length) {
        if (isAutofixTriggerMessage(messages[i].msg)) {
          batchUserMessages.push(messages[i].msg);
          i++;
          continue;
        }
        // A skip run wedged between two triggers must not end the batch:
        // the backend merges the surrounding triggers into one dispatch
        // with a single reply, so splitting here would strand the first
        // trigger as a forever-active pill. Consume the run only when a
        // trigger follows — otherwise leave it for the reply/mirror
        // lookaheads and the main-loop skip grouping.
        if (isAutofixSkippedMessage(messages[i].msg)) {
          let runEnd = i;
          while (
            runEnd < messages.length &&
            isAutofixSkippedMessage(messages[runEnd].msg)
          ) {
            runEnd++;
          }
          if (
            runEnd < messages.length &&
            isAutofixTriggerMessage(messages[runEnd].msg)
          ) {
            deferredSkipRuns.push(messages.slice(i, runEnd));
            i = runEnd;
            continue;
          }
        }
        break;
      }

      // Look past skip records a bot posted while the batch was running —
      // otherwise the reply would never pair and the pill would stay active
      // forever.
      // Mirror messages are never a trigger's reply — they report a
      // child-session batch and must stay in the stream to form their own
      // segment.
      let lookahead = i;
      while (
        lookahead < messages.length &&
        isAutofixSkippedMessage(messages[lookahead].msg)
      ) {
        lookahead++;
      }
      let agentMessage: AgentChatMessage | null = null;
      if (
        lookahead < messages.length &&
        messages[lookahead].msg.role === "agent" &&
        !isAutofixMirrorMessage(messages[lookahead].msg)
      ) {
        if (lookahead > i) deferredSkipRuns.push(messages.slice(i, lookahead));
        agentMessage = messages[lookahead].msg;
        i = lookahead + 1;
      }

      const { label, count } = buildIterationLabel(batchUserMessages);
      iterations.push({
        userMessages: batchUserMessages,
        agentMessage,
        label,
        issueCount: count,
      });

      // If the agent message was null (still pending) or is still active
      // (streaming/processing), stop — subsequent autofix user messages
      // should be treated as queued pending events, not new iterations.
      if (!agentMessage) break;
      if (agentMessage.isStreaming || agentMessage.isProcessing) break;
    }

    // Handle orphaned autofix user messages that were part of a merged
    // batch but got a higher message_idx than the agent response.
    // If the last iteration has no agent message and the preceding
    // iteration has a completed agent response, check if the orphaned
    // messages predate the response — if so, fold them in.
    if (
      iterations.length >= 2 &&
      !iterations[iterations.length - 1].agentMessage
    ) {
      const lastIter = iterations[iterations.length - 1];
      const prevIter = iterations[iterations.length - 2];
      if (
        prevIter.agentMessage &&
        !prevIter.agentMessage.isStreaming &&
        !prevIter.agentMessage.isProcessing
      ) {
        const cutoff =
          prevIter.agentMessage.updatedTimestamp ??
          prevIter.agentMessage.timestamp;
        const allPredate = lastIter.userMessages.every(
          (m) => m.timestamp <= cutoff,
        );
        if (allPredate) {
          prevIter.userMessages.push(...lastIter.userMessages);
          const { label, count } = buildIterationLabel(prevIter.userMessages);
          prevIter.label = label;
          prevIter.issueCount = count;
          iterations.pop();
        }
      }
    }

    // Detect pending (queued) autofix user messages that arrived while the
    // agent is still working on the current iteration. These go into
    // pendingMessages rather than starting a new iteration.
    const lastIteration = iterations[iterations.length - 1];
    const lastAgentActive =
      lastIteration.agentMessage &&
      (!!lastIteration.agentMessage.isStreaming ||
        !!lastIteration.agentMessage.isProcessing);

    const pendingMsgs: AgentChatMessage[] = [];
    if (lastAgentActive) {
      while (i < messages.length) {
        if (isAutofixTriggerMessage(messages[i].msg)) {
          pendingMsgs.push(messages[i].msg);
          i++;
          continue;
        }
        // Same rule as batch collection: a skip run between two queued
        // triggers is deferred rather than splitting the queue.
        if (isAutofixSkippedMessage(messages[i].msg)) {
          let runEnd = i;
          while (
            runEnd < messages.length &&
            isAutofixSkippedMessage(messages[runEnd].msg)
          ) {
            runEnd++;
          }
          if (
            runEnd < messages.length &&
            isAutofixTriggerMessage(messages[runEnd].msg)
          ) {
            deferredSkipRuns.push(messages.slice(i, runEnd));
            i = runEnd;
            continue;
          }
        }
        break;
      }
    }

    const totalIssues = iterations.reduce((sum, it) => sum + it.issueCount, 0);

    const isActive =
      !lastIteration.agentMessage ||
      !!lastIteration.agentMessage.isStreaming ||
      !!lastIteration.agentMessage.isProcessing;

    const { label: pendingLabel, count: pendingCount } =
      pendingMsgs.length > 0
        ? buildIterationLabel(pendingMsgs)
        : { label: "", count: 0 };

    const allMessageIds = new Set<string>();
    for (const it of iterations) {
      for (const um of it.userMessages) allMessageIds.add(um.id);
      if (it.agentMessage) allMessageIds.add(it.agentMessage.id);
    }
    for (const pm of pendingMsgs) allMessageIds.add(pm.id);

    const skippedFeedback = mergeSkippedFeedback(
      ...deferredSkipRuns.map((run) => summarizeSkippedRun(run)),
    );
    if (skippedFeedback) {
      for (const run of deferredSkipRuns) {
        for (const { msg: skipMsg } of run) allMessageIds.add(skipMsg.id);
      }
    }

    const group: AutofixGroup = {
      id: `autofix-group-${groupFirstMsg.id}`,
      iterations,
      totalIssues: totalIssues + pendingCount,
      isActive,
      pendingMessages: pendingMsgs,
      pendingLabel,
      pendingCount,
      allMessageIds,
      skippedFeedback,
    };

    result.push({
      kind: "autofix_group",
      group,
      msg: groupFirstMsg,
      originalIndex: groupFirstIndex,
    });
  }

  return result;
}

export type AutofixChildEntry = Extract<
  VisibleEntry,
  { kind: "autofix_child_group" }
>;

export type AutofixPillEntry = Extract<
  VisibleEntry,
  { kind: "autofix_child_group" | "autofix_group" }
>;

function isAutofixPillEntry(entry: VisibleEntry): entry is AutofixPillEntry {
  return entry.kind === "autofix_child_group" || entry.kind === "autofix_group";
}

/**
 * A transcript render slot. A run of two or more consecutive autofix pill
 * entries (per-PR child segments plus skip-only / parent-session groups,
 * e.g. "Ignored 3 events" next to "Addressed merge conflict") collapses
 * into one `autofix_pill_run` row, so the sibling pills share a single slot
 * in the transcript's message-spacing stack instead of each receiving the
 * full inter-message gap.
 */
export type TranscriptRow =
  | { kind: "entry"; entry: VisibleEntry }
  | { kind: "autofix_pill_run"; entries: AutofixPillEntry[] };

/**
 * Chunk the visible-entry list into transcript rows, merging runs of two or
 * more consecutive autofix pill entries. A lone pill stays a plain entry
 * row so its rendering is unchanged.
 */
export function chunkTranscriptRows(entries: VisibleEntry[]): TranscriptRow[] {
  const rows: TranscriptRow[] = [];
  let i = 0;
  while (i < entries.length) {
    if (!isAutofixPillEntry(entries[i])) {
      rows.push({ kind: "entry", entry: entries[i] });
      i++;
      continue;
    }
    const run: AutofixPillEntry[] = [];
    while (i < entries.length) {
      const next = entries[i];
      if (!isAutofixPillEntry(next)) break;
      run.push(next);
      i++;
    }
    if (run.length === 1) {
      rows.push({ kind: "entry", entry: run[0] });
    } else {
      rows.push({ kind: "autofix_pill_run", entries: run });
    }
  }
  return rows;
}

/**
 * Merge multiple autofix groups (e.g. the batches of a child sub-conversation
 * window) into a single group so the pill popover renders one continuous
 * iteration feed. Returns null for an empty list.
 */
export function combineAutofixGroups(
  groups: AutofixGroup[],
): AutofixGroup | null {
  if (groups.length === 0) return null;
  if (groups.length === 1) return groups[0];

  const iterations = groups.flatMap((g) => g.iterations);
  const pendingMessages = groups.flatMap((g) => g.pendingMessages);
  const { label: pendingLabel, count: pendingCount } =
    pendingMessages.length > 0
      ? buildIterationLabel(pendingMessages)
      : { label: "", count: 0 };
  const allMessageIds = new Set<string>();
  for (const g of groups) {
    g.allMessageIds.forEach((id) => allMessageIds.add(id));
  }

  return {
    id: groups[0].id,
    iterations,
    totalIssues:
      iterations.reduce((sum, it) => sum + it.issueCount, 0) + pendingCount,
    isActive: groups[groups.length - 1].isActive,
    pendingMessages,
    pendingLabel,
    pendingCount,
    allMessageIds,
    skippedFeedback: mergeSkippedFeedback(
      ...groups.map((g) => g.skippedFeedback),
    ),
  };
}

/**
 * True while an autofix batch is running on one of the session's managed
 * PRs. `active_autofix_batch` (derived from the running batch row) is the
 * source of truth for live autofix state — parent-thread `autofix_group`
 * message shapes are not: live work runs in child sessions and docks via
 * ActiveAutofixPillsBar, and any active-looking parent group is a stray
 * trigger the transcript no longer renders.
 */
export function hasActiveAutofixBatch(managedPrs?: ManagedPr[]): boolean {
  return (managedPrs ?? []).some((pr) => pr.active_autofix_batch != null);
}
